import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './Master.css';

const UserMaster = () => {
  const { users, setUsers, addAuditLog } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Clerk',
    fullName: '',
    contactNumber: '',
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);

  const roles = ['Admin', 'Judge', 'Clerk', 'Advocate', 'Public'];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingId) {
      const updatedUser = { ...formData };
      if (!updatedUser.password) {
        delete updatedUser.password;
      }
      setUsers(users.map(user => 
        user.id === editingId 
          ? { ...user, ...updatedUser }
          : user
      ));
      addAuditLog('User Updated', `User ${formData.username} updated`);
    } else {
      const newUser = {
        id: Date.now().toString(),
        ...formData,
        createdAt: new Date().toISOString(),
      };
      setUsers([...users, newUser]);
      addAuditLog('User Added', `New user ${formData.username} added`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'Clerk',
      fullName: '',
      contactNumber: '',
      isActive: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (user) => {
    setFormData({...user, password: ''});
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const user = users.find(u => u.id === id);
    setUsers(users.filter(u => u.id !== id));
    addAuditLog('User Deleted', `User ${user.username} deleted`);
  };

  const toggleUserStatus = (id) => {
    const user = users.find(u => u.id === id);
    setUsers(users.map(u => 
      u.id === id 
        ? { ...u, isActive: !u.isActive }
        : u
    ));
    addAuditLog('User Status Changed', `User ${user.username} ${user.isActive ? 'deactivated' : 'activated'}`);
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <h1>👤 User Master</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {showForm && (
        <div className="master-form-card">
          <h2>{editingId ? 'Edit User' : 'Add New User'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>{editingId ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingId}
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Contact Number</label>
                <input
                  type="tel"
                  value={formData.contactNumber}
                  onChange={(e) => setFormData({...formData, contactNumber: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Add'} User
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="master-table-card">
        <h2>User List ({users.length})</h2>
        {users.length > 0 ? (
          <div className="table-container">
            <table className="master-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td><strong>{user.username}</strong></td>
                    <td>{user.fullName}</td>
                    <td>{user.email}</td>
                    <td><span className="role-badge">{user.role}</span></td>
                    <td>{user.contactNumber || 'N/A'}</td>
                    <td>
                      <span className={user.isActive ? 'status-active' : 'status-inactive'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => handleEdit(user)} className="btn-edit">Edit</button>
                      <button onClick={() => toggleUserStatus(user.id)} className="btn-toggle">
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDelete(user.id)} className="btn-delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No users added yet. Click "Add User" to get started.</p>
        )}
      </div>
    </div>
  );
};

export default UserMaster;

